import { useMemo } from "react"
import { useStatusOrganizations } from "./useStatusOrganizations"
import { useOrganizationPermissions } from "./useOrganizationPermissions"
import { useAuth } from "./useAuth"

type RoleAccess = "no" | "view" | "full"
type Layout = "left" | "top"
type Props = { pageName: string } | void

export const useUserAccess = (props: Props) => {
  const userLayout = useMemo<Layout>(() => "left", [])
  const { currentOrgActive } = useStatusOrganizations()
  const { user } = useAuth()
  const searchCriteriaEnabled =
    user?.isAdmin === true || user?.searchCriteriaAccess === true

  // Get user permission level from the permission hook
  const { permissionLevel, isLoading, hasEditPermissionForPage } =
    useOrganizationPermissions()

  // Calculate access based on BOTH organization status AND user permission level
  const access = useMemo<RoleAccess>(() => {
    // Organization is disabled - view-only access regardless of user permissions
    if (!currentOrgActive) {
      return "view"
    }

    // Organization is active - access depends on user permission level
    if (permissionLevel === "FULL") {
      return "full"
    }

    // For users with EDIT permission, check the page-specific permissions
    if (permissionLevel === "EDIT") {
      return "view" // Default to view, will be overridden for specific pages based on hasEditPermissionForPage
    }

    if (permissionLevel === "READ") {
      return "view"
    }

    // Default fallback - view-only access
    return "view"
  }, [currentOrgActive, permissionLevel])

  const accessConfig = useMemo<Record<string, RoleAccess>>(() => {
    const searchCriteriaAccess = searchCriteriaEnabled ? access : "no"
    const adminOnlyAccess = user?.isAdmin === true ? access : "no"
    const config: Record<string, RoleAccess> = {
      Dashboard: access,
      Inbox: adminOnlyAccess,
      Prospects: access,
      Campaigns: access,
      "Campaigns Update": access,
      Sequences: access,
      "Sequences Update": access,
      Targeting: searchCriteriaAccess,
      "Targeting Update": searchCriteriaAccess,
      "Search Criteria": searchCriteriaAccess,
      "Master Lists": access,
      "Master Lists Update": access,
      Senders: access,
      "Email Addresses": access,
      Domains: access,
      "Domains Update": access,
      "DNC List": access,
      "Manage Organization": access,
      "Spam Testing": adminOnlyAccess,
    }

    // If organization is inactive, keep view only for all pages
    if (!currentOrgActive) {
      return config
    }

    // For EDIT users, update specific pages based on page permissions
    if (permissionLevel === "EDIT") {
      // Map page names to their URL paths for permission checking
      const pagePathMap: Record<string, string> = {
        Dashboard: "dashboard",
        Inbox: "inbox",
        Prospects: "prospects",
        Campaigns: "campaigns",
        "Campaigns Update": "campaigns",
        Sequences: "sequences",
        "Sequences Update": "sequences",
        Targeting: "search-criteria",
        "Targeting Update": "search-criteria",
        "Search Criteria": "search-criteria",
        "Master Lists": "master-lists",
        "Master Lists Update": "master-lists",
        Senders: "senders",
        "Email Addresses": "emails",
        Domains: "domains",
        "Domains Update": "domains",
        "DNC List": "dnc",
        "Manage Organization": "organization",
        "Spam Testing": "spam-testing",
      }

      // Check each page individually
      Object.keys(config).forEach((pageName) => {
        if (config[pageName] === "no") {
          return
        }
        const pagePath = pagePathMap[pageName]
        if (pagePath && hasEditPermissionForPage(pagePath)) {
          config[pageName] = "full"
        }
      })
    }

    return config
  }, [
    access,
    currentOrgActive,
    permissionLevel,
    hasEditPermissionForPage,
    searchCriteriaEnabled,
    user?.isAdmin,
  ])

  const accessedPages = useMemo(
    () =>
      Object.entries(accessConfig)
        .filter(([, access]) => access !== "no")
        .map(([page]) => page),
    [accessConfig],
  )

  const pageAccess = useMemo(() => {
    if (!props?.pageName) {
      return null
    }
    return accessConfig[props.pageName]
  }, [props, accessConfig])

  const pageFullAccess = useMemo(() => {
    if (!props?.pageName) {
      return false
    }

    // Get access from the config
    const configAccess = accessConfig[props.pageName]

    // For user-specific permission, full access is straightforward
    if (configAccess === "full") {
      return true
    }

    return false
  }, [props, accessConfig])

  const pageAnyAccess = useMemo(() => {
    if (!props?.pageName) {
      return false
    }
    return accessConfig[props.pageName] !== "no"
  }, [props, accessConfig])

  return {
    userLayout,
    accessedPages,
    pageAccess,
    pageFullAccess,
    pageAnyAccess,
    isLoading, // Expose the loading state so components can wait
  }
}
